import React from "react";
import { Linkedin } from "lucide-react";

/**
 * Team section — Yaalee co-founders.
 */

const MEMBERS = [
    {
        name: "Bhargabbi Baneerjee",
        role: "Co-Founder & CEO",
        bio: "Sets the vision and steers Yaalee's mission — where science meets community impact.",
        photo:
            "https://customer-assets.emergentagent.com/job_bio-guardian/artifacts/ilffadbk_image.png",
        linkedin: "https://www.linkedin.com/in/bhargabbi-baneerjee/",
    },
    {
        name: "Charu Manihar",
        role: "Co-Founder & Chief Strategy Officer",
        bio: "Architect of partnerships, go-to-market and grassroots growth strategy.",
        photo:
            "https://customer-assets.emergentagent.com/job_bio-guardian/artifacts/fvp9vb9f_image.png",
        linkedin: "https://www.linkedin.com/in/charumanihar/",
    },
    {
        name: "Dr. Samuel Jacob",
        role: "Co-Founder & Chief Product Officer",
        bio: "Translates rigorous biotech research into tangible, market-ready sustainable products.",
        photo:
            "https://customer-assets.emergentagent.com/job_bio-guardian/artifacts/3v6gakgd_image.png",
        linkedin:
            "https://www.linkedin.com/in/dr-samuel-jacob-10679225/?skipRedirect=true",
    },
];

export const Team = () => {
    return (
        <section
            id="team"
            data-testid="team-section"
            className="section-pad relative overflow-hidden"
        >
            <div
                aria-hidden
                className="blob bg-[hsl(var(--brand-copper))]/12"
                style={{ width: 560, height: 560, top: -160, right: -120 }}
            />
            <div
                aria-hidden
                className="blob bg-[hsl(var(--brand-moss))]/12"
                style={{ width: 520, height: 520, bottom: -200, left: -180 }}
            />

            <div className="container-x relative">
                <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-end mb-16">
                    <div className="lg:col-span-7">
                        <p className="overline">
                            <span className="eyebrow-line" />
                            The founders
                        </p>
                        <h2 className="mt-5 font-display text-4xl sm:text-5xl lg:text-6xl tracking-tighter font-medium leading-[1.02]">
                            The Yaalee{" "}
                            <em className="not-italic text-primary">guardians</em>.
                        </h2>
                    </div>
                    <p className="lg:col-span-5 text-base md:text-lg leading-relaxed text-muted-foreground">
                        A scientist, a strategist and a builder — three disciplines
                        woven together like the Yali itself, each bringing a distinct
                        strength to the mission.
                    </p>
                </div>

                <div className="grid grid-cols-3 gap-3 sm:gap-4 md:gap-6 max-w-5xl mx-auto">
                    {MEMBERS.map((m, i) => (
                        <article
                            key={m.name}
                            data-testid={`team-card-${i}`}
                            className="group relative flex flex-col rounded-2xl md:rounded-[1.75rem] border border-border bg-card/60 backdrop-blur-sm overflow-hidden transition-all duration-500 hover:border-primary/60 hover:-translate-y-1 hover:shadow-2xl hover:shadow-primary/10"
                        >
                            {/* Portrait — compact square on mobile, taller on desktop */}
                            <div className="relative aspect-square sm:aspect-[5/6] overflow-hidden bg-card">
                                <img
                                    src={m.photo}
                                    alt={`${m.name} portrait`}
                                    className="absolute inset-0 w-full h-full object-cover object-top transition-transform duration-[1800ms] group-hover:scale-[1.03]"
                                />
                            </div>

                            {/* Info block */}
                            <div className="relative flex flex-col flex-1 p-3 sm:p-5 md:p-6">
                                <h3 className="font-display text-[15px] sm:text-[20px] md:text-[24px] font-medium tracking-tight leading-tight">
                                    {m.name}
                                </h3>
                                <p className="mt-1.5 text-[8.5px] sm:text-[10px] md:text-[11px] uppercase tracking-[0.18em] md:tracking-[0.22em] text-primary font-semibold">
                                    {m.role}
                                </p>
                                <div className="hidden sm:block mt-4 h-px w-10 bg-border group-hover:w-16 group-hover:bg-primary transition-all duration-500" />
                                <p className="hidden sm:block mt-4 text-[13px] md:text-[14px] leading-relaxed text-muted-foreground">
                                    {m.bio}
                                </p>

                                <div className="mt-auto pt-6 flex items-center justify-end">
                                    <a
                                        href={m.linkedin}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        data-testid={`team-linkedin-${i}`}
                                        aria-label={`${m.name} on LinkedIn`}
                                        className="inline-flex h-8 w-8 md:h-9 md:w-9 items-center justify-center rounded-full border border-border bg-background/60 text-muted-foreground hover:border-primary hover:bg-primary hover:text-primary-foreground transition-colors"
                                    >
                                        <Linkedin className="h-3.5 w-3.5 md:h-4 md:w-4" />
                                    </a>
                                </div>
                            </div>
                        </article>
                    ))}
                </div>

                <div className="mt-14 text-center">
                    <span className="inline-block text-[11px] uppercase tracking-[0.22em] text-muted-foreground divider-leaf">
                        Science · Strategy · Stewardship
                    </span>
                </div>
            </div>
        </section>
    );
};

export default Team;
